import { type ReactElement, useCallback, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom-v5-compat";
import type { Prices } from "@b2bportal/lodging-api";
import { usePriceFormatterContext } from "@hopper-b2b/contexts";
import { I18nMarkup, useI18nContext } from "@hopper-b2b/i18n";
import {
  ActionLink,
  Carousel,
  IconComponent,
  IconName,
  Slot,
  StarRating,
} from "@hopper-b2b/ui";
import { useDeviceTypes, useFeatureFlagsContext } from "@hopper-b2b/utilities";
import { Typography } from "@material-ui/core";
import clsx from "clsx";
import { useLodgingLink } from "../../hooks/useLodgingLink";
import { TripAdvisor } from "../LodgingCard/components/TripAdvisor";
import type { BasicLodgingInfo } from "./LodgingMap";

interface IMapMarker extends BasicLodgingInfo {
  lodgingId: string;
  available: boolean;
  lodgingName: string;
  lat: number;
  lng: number;
  selected: boolean;
  isOveredInLodgingList: boolean;
  price: Prices;
  priceWithNoDiscounts?: Prices;
  onClick?: (id: string, lat: number, lng: number) => void;
  onClose?: (e: Event) => void;
  onOpenQuickView?: (offsetY: number) => void;
}

export const Markers = ({
  lodgingId,
  available,
  lodgingName,
  selected = false,
  isOveredInLodgingList = false,
  lat,
  lng,
  onClick,
  name,
  media,
  starRating,
  tripAdvisor,
  price,
  priceWithNoDiscounts,
  totalPrice,
  onClose,
  onOpenQuickView,
}: IMapMarker): ReactElement => {
  const { matchesMobile } = useDeviceTypes();
  const location = useLocation();
  const { brand } = useI18nContext();
  const { formatPrice } = usePriceFormatterContext();
  const quickViewRef = useRef(null);
  const onSelect = useCallback(() => {
    onClick(lodgingId, lat, lng);
  }, [lat, lng, lodgingId, onClick]);

  const lodgingLink = useLodgingLink(lodgingId, lodgingName);
  // Tenant defined UX -  Hide Lodging card in map when marker is selected when showing a horizontal
  // list of lodgings overlaying map
  const featureFlags = useFeatureFlagsContext();
  const isHorizontalScrollEnabled =
    Boolean(featureFlags.showHorizontalScrollListInMobileMap) && matchesMobile;

  useEffect(() => {
    if (selected && quickViewRef?.current) {
      onOpenQuickView(quickViewRef.current.offsetHeight / 2);
    }
  }, [onOpenQuickView, selected]);

  const formattedPrice = available
    ? formatPrice(price, {
        maximumFractionDigits: brand?.lodgingCardPriceRounding || 0,
        minimumFractionDigits: brand?.lodgingCardPriceRounding || 0,
        roundingMode: "ceil",
      })
    : "";

  const formattedPriceWithNoDiscounts = priceWithNoDiscounts
    ? formatPrice(priceWithNoDiscounts, {
        maximumFractionDigits: brand?.lodgingCardPriceRounding || 0,
        minimumFractionDigits: brand?.lodgingCardPriceRounding || 0,
        roundingMode: "ceil",
      })
    : null;

  return (
    <>
      <button
        type="button"
        className={clsx("marker", {
          selected,
          isOveredInLodgingList,
          unavailable: formattedPrice.length === 0,
        })}
        onClick={onSelect}
      >
        <Typography color="inherit" noWrap>
          {formattedPrice}
        </Typography>
      </button>
      {(!isHorizontalScrollEnabled || !matchesMobile) && selected ? (
        <Link
          className="quick-view-container"
          to={
            available ? lodgingLink : `${location.pathname}${location.search}`
          }
          target={matchesMobile || !available ? "" : "_blank"}
          ref={quickViewRef}
        >
          <div className="close-container">
            <ActionLink
              content={<IconComponent name={IconName.Close} />}
              onClick={onClose}
            />
          </div>
          <div className="quick-view-carousel">
            <Carousel
              imageUrlsArray={media?.map((asset) => asset.url)}
              onClick={null}
            />
          </div>
          <Typography variant="h2">{name}</Typography>
          <div className="quick-view-ratings">
            <StarRating
              className="quick-view-ratings-starRating"
              starRating={starRating}
            />
            <TripAdvisor
              className="quick-view-ratings-reviews"
              tripAdvisorReviews={tripAdvisor}
            />
          </div>

          <Slot id="lodging-card-policy" lodgingId={lodgingId} />
          {available ? (
            <>
              <div className="price-container">
                {formattedPriceWithNoDiscounts && (
                  <Typography className="price-with-no-discount">
                    {formattedPriceWithNoDiscounts}
                  </Typography>
                )}
                <Typography variant="h2">
                  <I18nMarkup
                    tKey={"pricePerNight"}
                    replacements={{
                      price: formattedPrice,
                    }}
                  />
                </Typography>
              </div>
              <Typography variant="body1">{totalPrice}</Typography>
            </>
          ) : null}
        </Link>
      ) : null}
    </>
  );
};

export const ClusterMarker = ({
  cluster,
  lat,
  lng,
  expansionZoom,
  isOveredInLodgingList,
  onClick,
}) => {
  const { t } = useI18nContext();

  const onClusterClick = useCallback(() => {
    onClick(lat, lng, expansionZoom);
  }, [expansionZoom, lat, lng, onClick]);
  return (
    <button
      type="button"
      className={clsx("cluster-marker", {
        isOveredInLodgingList,
      })}
      onClick={onClusterClick}
    >
      <Typography color="inherit" noWrap>
        {t("stays", { count: cluster.properties.point_count })}
      </Typography>
    </button>
  );
};
